import React from 'react';
import ReactDOMServer from 'react-dom/server';

const getPalette = (theme) => {
    const palette = theme?.palette || {};
    return {
        background: palette.background?.paper || '#ffffff',
        textPrimary: palette.text?.primary || '#1a1a1a',
        textSecondary: palette.text?.secondary || '#5f6368',
        divider: palette.divider || 'rgba(0,0,0,0.12)'
    };
};

const defaultValueFormatter = (value) => {
    if (value === null || value === undefined) return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return String(value);
    const hasDecimals = Math.abs(num % 1) > 0;
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: hasDecimals ? 2 : 0 }).format(num);
};

const defaultLabelFormatter = (label) => {
    if (label === null || label === undefined) return '';
    const text = String(label);
    const date = new Date(text);
    if (!Number.isNaN(date.getTime())) {
        return date.toLocaleDateString();
    }
    return text;
};

const resolveLabel = (w, dataPointIndex, seriesIndex) => {
    const globals = w?.globals || {};
    const config = w?.config || {};
    return (
        globals?.categoryLabels?.[dataPointIndex] ??
        globals?.labels?.[dataPointIndex] ??
        config?.labels?.[dataPointIndex] ??
        config?.xaxis?.categories?.[dataPointIndex] ??
        config?.series?.[seriesIndex]?.data?.[dataPointIndex]?.x ??
        config?.series?.[0]?.data?.[dataPointIndex]?.x ??
        ''
    );
};

export const renderTrendTooltip = (theme, { title, rows, minWidth = 240, footer }) => {
    const palette = getPalette(theme);
    const safeRows = Array.isArray(rows) ? rows.filter(Boolean) : [];
    const safeTitle = title ? String(title) : '';

    return ReactDOMServer.renderToString(
        <div
            style={{
                background: palette.background,
                minWidth,
                padding: '10px 12px',
                color: palette.textPrimary,
                borderRadius: 8,
                border: `1px solid ${palette.divider}`
            }}
        >
            {safeTitle ? (
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: safeRows.length ? 8 : 0 }}>
                    {safeTitle}
                </div>
            ) : null}
            {safeRows.length ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 4, columnGap: 12, fontSize: 12 }}>
                    {safeRows.map((row, index) => (
                        <React.Fragment key={`${row.label}-${index}`}>
                            <div style={{ color: row.labelColor || palette.textPrimary }}>
                                {row.label}
                            </div>
                            <div style={{ fontWeight: row.valueWeight ?? 600, color: row.valueColor || palette.textPrimary }}>
                                {row.value}
                            </div>
                        </React.Fragment>
                    ))}
                </div>
            ) : null}
            {footer ? (
                <div style={{ marginTop: 8, fontSize: 11, color: palette.textSecondary }}>
                    {footer}
                </div>
            ) : null}
        </div>
    );
};

export const buildApexTooltip = (
    theme,
    {
        valueFormatter,
        labelFormatter,
        minWidth = 240,
        showSeriesName = true,
        shared = true,
        intersect = false,
    } = {}
) => {
    const formatValue = typeof valueFormatter === 'function' ? valueFormatter : defaultValueFormatter;
    const formatLabel = typeof labelFormatter === 'function' ? labelFormatter : defaultLabelFormatter;

    return {
        shared,
        intersect,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const rawLabel = resolveLabel(w, dataPointIndex, seriesIndex);
            const title = formatLabel(rawLabel, { seriesIndex, dataPointIndex, w });
            const chartType = w?.config?.chart?.type || w?.globals?.chartType;
            const isPie = ['pie', 'donut', 'radialBar'].includes(chartType);

            if (isPie) {
                const sliceLabel =
                    rawLabel ||
                    w?.config?.labels?.[seriesIndex] ||
                    w?.globals?.labels?.[seriesIndex] ||
                    'Value';
                const value = Array.isArray(series) ? series?.[seriesIndex] : series;
                const rows = [
                    {
                        label: showSeriesName ? 'Value' : sliceLabel,
                        value: formatValue(value, { seriesIndex, dataPointIndex, label: sliceLabel, w })
                    }
                ];
                return renderTrendTooltip(theme, { title: sliceLabel, rows, minWidth });
            }

            const seriesList = w?.config?.series || [];
            const rows = seriesList
                .map((item, idx) => {
                    const value = Array.isArray(series?.[idx]) ? series[idx][dataPointIndex] : null;
                    if (value === null || value === undefined) return null;
                    const name = item?.name || `Series ${idx + 1}`;
                    return {
                        label: showSeriesName ? name : 'Value',
                        value: formatValue(value, { seriesIndex: idx, dataPointIndex, label: title, w })
                    };
                })
                .filter(Boolean);

            if (!title && rows.length === 0) return '';
            return renderTrendTooltip(theme, { title, rows, minWidth });
        }
    };
};

export const applyTrendTooltip = (theme, options, { valueFormatter, labelFormatter, minWidth, showSeriesName } = {}) => {
    const base = options && typeof options === 'object' ? options : {};
    const existing = base.tooltip || {};
    if (existing?.enabled === false || existing?.custom) return base;
    const formatter = valueFormatter || existing?.y?.formatter;
    const tooltip = buildApexTooltip(theme, {
        valueFormatter: formatter,
        labelFormatter,
        minWidth,
        showSeriesName,
        shared: existing?.shared ?? true,
        intersect: existing?.intersect ?? false,
    });
    return {
        ...base,
        tooltip: {
            ...existing,
            ...tooltip
        }
    };
};
